<template>
  <div v-if="isLoading" class="isLoadingDiv">
    <ProgressSpinner />
  </div>

  <div v-else class="p-text-left p-mt-6">
    <UpgradeBanner v-if="currentUser && !currentUser.upgraded" />
    <h1 class="title is-3">
      {{ $t("standings") }}
    </h1>

    <Button class="p-button-sm p-button-secondary my-3" @click="copyShareLink">
      {{ $t("share") }}
    </Button>

    <div class="card">
      <TabView>
        <TabPanel :header="$t('league')" class="leaderboard">
          <h5 class="title p-text-left is-5 mt-3">
            {{ this.$store.getters.currentLeague.name }}
          </h5>

          <div class="p-my-4">
            <div class="p-d-flex p-justify-between p-align-baseline">
              <div class="p-d-flex">
                <div class="mr-5">
                  <span>{{ $t("win") }}</span>
                  {{ $t("this-scoringsystem-pts", [this.scoringSystem.win_points]) }}
                </div>

                <div class="mr-5">
                  <span>{{ $t("loss") }}</span>
                  {{ $t("this-scoringsystem-pts", [this.scoringSystem.loss_points]) }}
                </div>

                <div class="mr-5">
                  <span>{{ $t("tie") }}</span>
                  {{ $t("this-scoringsystem-pts", [this.scoringSystem.tie_points]) }}
                </div>

                <div class="mr-5">
                  <span>{{ $t("overtime-win") }}</span>
                  {{ $t("this-scoringsystem-pts", [this.scoringSystem.overtime_win_points]) }}
                </div>

                <div class="mr-5">
                  <span>{{ $t("overtime-loss") }}</span>
                  {{ $t("this-scoringsystem-pts", [this.scoringSystem.overtime_loss_points]) }}
                </div>
              </div>

              <Button class="p-button-sm p-button-secondary" @click="scoringSystemModal = true">
                {{ $t("change-scoring-system") }}
              </Button>
            </div>
          </div>

          <DataTable
            :scrollable="true"
            class="p-datatable-sm p-datatable-striped whiteBg mt-5"
            dataKey="id"
            :value="leagueDivisions"
          >
            <Column field="name" :header="$t('standingHeaders.team')"></Column>
            <Column field="gamesPlayed" :header="$t('standingHeaders.gamesPlayed')"></Column>
            <Column field="win" :header="$t('standingHeaders.win')"></Column>
            <Column field="loss" :header="$t('standingHeaders.loss')"></Column>
            <Column field="tie" :header="$t('standingHeaders.tie')"></Column>
            <Column field="win_pct" :header="$t('standingHeaders.winPct')"></Column>
            <Column field="overtime_win" :header="$t('standingHeaders.otWin')"></Column>
            <Column field="overtime_loss" :header="$t('standingHeaders.otLoss')"></Column>
            <Column field="home">
              <template #header="slotProps">
                {{ $t("standingHeaders.home") }}
                <font-awesome-icon
                  :icon="['far', 'question-circle']"
                  class="tooltip-icon"
                  v-tooltip.top="$t('standingHeaders.tooltipText')"
                />
              </template>
            </Column>
            <Column field="away">
              <template #header="slotProps">
                {{ $t("standingHeaders.away") }}
                <font-awesome-icon
                  :icon="['far', 'question-circle']"
                  class="tooltip-icon"
                  v-tooltip.top="$t('standingHeaders.tooltipText')"
                />
              </template>
            </Column>
            <Column field="points" :header="$t('standingHeaders.points')"></Column>
            <Column field="rank" :header="$t('standingHeaders.rank')"></Column>
          </DataTable>
        </TabPanel>

        <TabPanel :header="$t('division')">
          <div v-for="(stats, index) in statsByDivision" :key="`division-${index}`">
            <div v-if="stats.length" class="mb-6 leaderboard">
              <h5 class="title p-text-left is-5 mt-3">
                {{ stats[0].divisionName }}
              </h5>

              <DataTable
                :scrollable="true"
                class="p-datatable-sm p-datatable-striped whiteBg mt-5"
                dataKey="id"
                :value="stats"
              >
                <Column field="name" :header="$t('standingHeaders.team')"></Column>
                <Column field="gamesPlayed" :header="$t('standingHeaders.gamesPlayed')"></Column>
                <Column field="win" :header="$t('standingHeaders.win')"></Column>
                <Column field="loss" :header="$t('standingHeaders.loss')"></Column>
                <Column field="tie" :header="$t('standingHeaders.tie')"></Column>
                <Column field="win_pct" :header="$t('standingHeaders.winPct')"></Column>
                <Column field="overtime_win" :header="$t('standingHeaders.otWin')"></Column>
                <Column field="overtime_loss" :header="$t('standingHeaders.otLoss')"></Column>
                <Column field="home">
                  <template #header="slotProps">
                    {{ $t("standingHeaders.home") }}
                    <font-awesome-icon
                      :icon="['far', 'question-circle']"
                      class="tooltip-icon"
                      v-tooltip.top="$t('standingHeaders.tooltipText')"
                    />
                  </template>
                </Column>
                <Column field="away">
                  <template #header="slotProps">
                    {{ $t("standingHeaders.away") }}
                    <font-awesome-icon
                      :icon="['far', 'question-circle']"
                      class="tooltip-icon"
                      v-tooltip.top="$t('standingHeaders.tooltipText')"
                    />
                  </template>
                </Column>
                <Column field="points" :header="$t('standingHeaders.points')"></Column>
                <Column field="rank" :header="$t('standingHeaders.rank')"></Column>
              </DataTable>
            </div>
          </div>
        </TabPanel>

        <TabPanel :header="$t('conference')"  class="leaderboard">
          <div class="pb-6">
            <Button class="p-button-sm p-button-secondary floatRight" @click="createConferenceModal = true">
              {{ $t("create-conference") }}
            </Button>
          </div>

          <div v-for="(conference, index) in conferences" :key="conference.id" class="mb-6">
            <h5 class="title p-text-left is-5 mt-3">
              {{ conference.name }}

              <font-awesome-icon
                @click="() => deleteConference(conference.id)"
                class="menuIcon floatRight cursorpointer ml-5"
                :icon="['fat', 'xmark']"
              />
            </h5>

            <DataTable
              :scrollable="true"
              class="p-datatable-sm p-datatable-striped whiteBg mt-5"
              dataKey="id"
              :value="stats"
            >
              <Column field="name" :header="$t('standingHeaders.team')"></Column>
              <Column field="gamesPlayed" :header="$t('standingHeaders.gamesPlayed')"></Column>
              <Column field="win" :header="$t('standingHeaders.win')"></Column>
              <Column field="loss" :header="$t('standingHeaders.loss')"></Column>
              <Column field="tie" :header="$t('standingHeaders.tie')"></Column>
              <Column field="win_pct" :header="$t('standingHeaders.winPct')"></Column>
              <Column field="overtime_win" :header="$t('standingHeaders.otWin')"></Column>
              <Column field="overtime_loss" :header="$t('standingHeaders.otLoss')"></Column>
              <Column field="home">
                <template #header="slotProps">
                  {{ $t("standingHeaders.home") }}
                  <font-awesome-icon
                    :icon="['far', 'question-circle']"
                    class="tooltip-icon"
                    v-tooltip.top="$t('standingHeaders.tooltipText')"
                  />
                </template>
              </Column>
              <Column field="away">
                <template #header="slotProps">
                  {{ $t("standingHeaders.away") }}
                  <font-awesome-icon
                    :icon="['far', 'question-circle']"
                    class="tooltip-icon"
                    v-tooltip.top="$t('standingHeaders.tooltipText')"
                  />
                </template>
              </Column>
              <Column field="points" :header="$t('standingHeaders.points')"></Column>
              <Column field="rank" :header="$t('standingHeaders.rank')"></Column>
            </DataTable>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <ManageScoringSystemModal
      :active="scoringSystemModal"
      :input="{ ...scoringSystem }"
      :close="() => (scoringSystemModal = false)"
      :updateScoringSystemValues="updateScoringSystemValues"
    />
    <CreateConferenceModal
      :active="createConferenceModal"
      :close="() => (createConferenceModal = false)"
      :teams="leagueTeams"
      :createConference="createConference"
    />
  </div>
</template>

<script>
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import api from "@/api";
import ManageScoringSystemModal from "@/components/ManageScoringSystemModal.vue";
import CreateConferenceModal from "@/components/CreateConferenceModal.vue";

export default {
  components: { ManageScoringSystemModal, CreateConferenceModal, TabView, TabPanel, DataTable, Column },
  data() {
    return {
      isLoading: false,
      leagueDivisions: [],
      leagueTeams: [],
      statsByDivision: [],
      conferences: [],
      conferencesStats: [],
      scoringSystem: {
        win_points: 3,
        loss_points: 0,
        tie_points: 2,
        overtime_win_points: 1,
        overtime_loss_points: 0,
      },
      scoringSystemModal: false,
      createConferenceModal: false,
    };
  },
  methods: {
    fetchData() {
      const seasonScoringSystem = this.$store.getters.currentSeason.scoring_system;

      if (seasonScoringSystem.win_points) {
        this.scoringSystem = seasonScoringSystem;
      }

      api.getDivisions(this.$store.getters.currentSeason.id).then(response => {
        const teamMap = new Map();
        this.leagueTeams = response.data
          .map(d => d.teams)
          .flat()
          .filter(t => {
            if (teamMap.get(t.id)) return false;
            teamMap.set(t.id, t);
            return true;
          });

        const allLeagueGames = response.data.map(d => d.games).flat();
        const allSeasonGames = response.data
          .filter(d => d.season.id === this.$store.getters.currentSeason.id)
          .map(d => d.games)
          .flat();

        const statsByDivision = this.leaderboardStatsByDivision(allSeasonGames, this.scoringSystem);
        this.statsByDivision = statsByDivision;

        console.log("[Debug] allSeasonGames:", allSeasonGames);
        console.log("[Debug] statsByDivision:", statsByDivision);

        this.leagueDivisions = this.gamesLeaderboardStats(allLeagueGames, this.scoringSystem)
          .sort((a, b) => b.points - a.points || b.gamesPlayed - a.gamesPlayed)
          .map((team, index) => ({ ...team, rank: index + 1 }));
      });

      api.getConferences(this.$store.getters.currentSeason.id).then(response => {
        this.conferences = response.data;
        this.conferencesStats = response.data.map(conference => {
          const stats = this.gamesLeaderboardStats(conference.games, this.scoringSystem);
          const teamNames = conference.teams.map(t => t.name);
          const filteredStats = stats.filter(stat => teamNames.includes(stat.name));

          const filteredTeams = filteredStats.map(team => team.name);
          const teamsWithNoGames = teamNames.filter(n => !filteredTeams.includes(n));
          teamsWithNoGames.forEach(teamName => filteredStats.push(this.buildEmptyGameStatRow(teamName)));

          return filteredStats.map((team, index) => ({ ...team, rank: index + 1 }));
        });
      });
    },
    updateScoringSystemValues(values) {
      api.updateScoringSystem(this.$store.getters.currentSeason.scoring_system.id, values).then(() => {
        this.makeToast("info", this.$t("scoring-system-has-been-changed"));
        this.scoringSystem = values;

        const currentSeason = this.$store.getters.currentSeason;
        currentSeason.scoring_system = values;
        this.$store.commit("setCurrentSeason", currentSeason);
      });
      this.fetchData();
    },
    createConference(conference) {
      api.createConference(conference).then(response => {
        this.makeToast("success", this.$t("conference-response-data-name-created", [response.data.name]));
        this.fetchData();
        this.createConferenceModal = false;
      });
    },
    deleteConference(conferenceId) {
      if (confirm(this.$t("are-you-sure-you-want-to-delete-this-conference"))) {
        api.deleteConference(conferenceId).then(() => {
          this.makeToast("info", this.$t("conference-has-been-deleted"));
          this.fetchData();
        });
      }
    },
    copyShareLink() {
      const webpageDomain = this.$store.getters.currentOrganization.webpage_url;
      this.$copyText(`${window.location.origin}/${webpageDomain}`).then(() => {
        this.makeToast("success", this.$t("copied-share-link-to-clipboard"));
      });
    },
  },
  created() {
    this.fetchData();
  },
};
</script>
